'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Combobox,
  createListCollection,
  IconButton,
  Input,
  Spinner,
  Text,
} from '@terra/ui-design-system';
import { Box, HStack, token } from '@terra/ui-styled-system';
import { Search01Icon, Cancel01Icon } from '@terra/ui-icons';
import type { InternalNetworkAdEntitySummaryDto } from '@terra/shared-contracts-internal-network';
import { GRAPH_NODE_ICON } from '@terra/ui-graphs/internal-network';
import { useDebounceValue } from 'usehooks-ts';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../clients/testerClient';

const MIN_SEARCH_LENGTH = 2;

type InternalNetworkAdEntitySearchComboboxProps = {
  networkId: string;
  placeholder: string;
  onSelect: (entity: InternalNetworkAdEntitySummaryDto) => void;
};
type SearchComboboxItem = InternalNetworkAdEntitySummaryDto & {
  value: string;
  label: string;
};

export function InternalNetworkAdEntitySearchCombobox({
  networkId,
  placeholder,
  onSelect,
}: InternalNetworkAdEntitySearchComboboxProps) {
  const t = useTranslations('internalNetwork.graph');
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput] = useDebounceValue(inputValue, 250);
  const [entities, setEntities] = useState<InternalNetworkAdEntitySummaryDto[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = debouncedInput.trim();

    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setEntities([]);
      setIsLoading(false);

      return;
    }

    const requestId = requestIdRef.current + 1;

    requestIdRef.current = requestId;
    setIsLoading(true);
    void terraProxyApiClient
      .searchInternalNetworkAdEntities({
        networkId,
        term: trimmed,
        limit: 25,
      })
      .then((result: { entities: InternalNetworkAdEntitySummaryDto[] }) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        setEntities(result.entities);
      })
      .catch((error: unknown) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        console.warn('[InternalNetworkAdEntitySearch] Search failed', error);
        setEntities([]);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [debouncedInput, networkId]);
  const items = useMemo<SearchComboboxItem[]>(
    () =>
      entities.map((entity) => ({
        ...entity,
        label: entity.displayName,
        value: entity.nodeId,
      })),
    [entities],
  );
  const collection = useMemo(
    () =>
      createListCollection({
        items,
      }),
    [items],
  );

  return (
    <Combobox.Root
      collection={collection}
      inputValue={inputValue}
      onInputValueChange={(details) => setInputValue(details.inputValue)}
      onValueChange={(details) => {
        const nodeId = details.value[0];
        const entity = entities.find((item) => item.nodeId === nodeId);

        if (entity) {
          onSelect(entity);
          setInputValue(entity.displayName);
        }
      }}
      width="full"
      size="sm"
      positioning={{ sameWidth: true, strategy: 'fixed' }}
      openOnClick={inputValue.trim().length >= MIN_SEARCH_LENGTH}
    >
      <Box as="label" position="relative" w="full">
        {isLoading ? (
          <Box
            position="absolute"
            left="8px"
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
            display="flex"
            alignItems="center"
          >
            <Spinner size="sm" />
          </Box>
        ) : (
          <Search01Icon
            size={16}
            color={token('colors.fg.neutral.subtle')}
            style={{
              position: 'absolute',
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
        <Combobox.Control>
          <Combobox.Input placeholder={placeholder} asChild>
            <Input
              size="sm"
              h="32px"
              minH="32px"
              pl="10"
              pr="8"
              borderRadius="full"
              w="full"
              bg="bg.neutral.default"
              _hover={{ bg: 'bg.neutral.subtleSoft' }}
            />
          </Combobox.Input>
        </Combobox.Control>
        {inputValue ? (
          <IconButton
            type="button"
            variant="link"
            size="xs"
            aria-label={t('clearSearch')}
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            borderRadius="full"
            w="20px"
            h="20px"
            minW="20px"
            minH="20px"
            bg="bg.neutral.subtle"
            color="fg.default"
            _hover={{ bg: 'bg.neutral.subtleSoft' }}
            onClick={() => setInputValue('')}
          >
            <Cancel01Icon size={14} color={token('colors.fg.default')} />
          </IconButton>
        ) : null}
      </Box>
      <Combobox.Positioner>
        <Combobox.Content maxH="280px" overflowY="auto">
          {entities.length === 0 && !isLoading ? (
            <Box px={3} py={3}>
              <Text fontSize="sm" color="fg.muted">
                {debouncedInput.trim().length < MIN_SEARCH_LENGTH
                  ? 'Type at least 2 characters'
                  : 'No entities found'}
              </Text>
            </Box>
          ) : (
            <Combobox.ItemGroup>
              {items.map((entity) => {
                const Icon = GRAPH_NODE_ICON[entity.entityKind];

                return (
                  <Combobox.Item key={entity.value} item={entity}>
                    <HStack gap={2} alignItems="center" minW={0}>
                      {Icon ? (
                        <Box
                          w={5}
                          h={5}
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon size={14} />
                        </Box>
                      ) : null}
                      <Combobox.ItemText>{entity.label}</Combobox.ItemText>
                    </HStack>
                  </Combobox.Item>
                );
              })}
            </Combobox.ItemGroup>
          )}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  );
}
